import { getAllBlogSlugs } from '@/services/flotiqApi';
import { getAllCityIndustryUrls } from '@/components/CityIndustryRouteHandler';

/**
 * Generates XML content for the blog sitemap
 */
export async function generateBlogSitemap(): Promise<string> {
  try {
    // Fetch all blog post slugs from Flotiq
    const slugs = await getAllBlogSlugs();
    
    // Start the XML document
    let sitemap = '<?xml version="1.0" encoding="UTF-8"?><urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
    
    // Add the blog main page
    sitemap += '  <!-- Blog main page -->\n';
    sitemap += '  <url>\n';
    sitemap += '    <loc>https://astrem.co/blog</loc>\n';
    sitemap += '    <changefreq>weekly</changefreq>\n';
    sitemap += '    <priority>0.8</priority>\n';
    sitemap += '  </url>\n';
    
    // Add each blog post
    sitemap += '  <!-- Individual blog posts -->\n';
    for (const slug of slugs) {
      sitemap += '  <url>\n';
      sitemap += `    <loc>https://astrem.co/blog/${slug}</loc>\n`;
      sitemap += '    <changefreq>monthly</changefreq>\n';
      sitemap += '    <priority>0.7</priority>\n';
      sitemap += '  </url>\n';
    }
    
    // Close the XML document
    sitemap += '</urlset>';
    
    return sitemap;
  } catch (error) {
    console.error('Error generating blog sitemap:', error);
    
    // Return a basic sitemap with just the blog main page if there's an error
    return `<?xml version="1.0" encoding="UTF-8"?><urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <!-- Blog main page -->
  <url>
    <loc>https://astrem.co/blog</loc>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>
  <!-- Error loading blog posts -->
</urlset>`;
  }
}

/**
 * Writes the generated sitemap to the public/blog/sitemap.xml file
 * This function is meant to be called during the build process
 */
export async function writeBlogSitemap() {
  // This function would be used in a build script
  // In a Vite project, we would need a build plugin or script to call this
  const sitemap = await generateBlogSitemap();
  // In a build script, we would write this to file using Node.js fs module
  return sitemap;
}

/**
 * Generate full website sitemap including all city-industry pages
 */
export function generateMainSitemap(): string {
  // Get all city-industry URLs
  const cityIndustryUrls = getAllCityIndustryUrls();
  
  let sitemap = '<?xml version="1.0" encoding="UTF-8"?><urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
  
  // Add main pages
  sitemap += '  <!-- Main pages -->\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/</loc>\n';
  sitemap += '    <changefreq>weekly</changefreq>\n';
  sitemap += '    <priority>1.0</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/about</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.8</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/services</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.8</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/portfolio</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.8</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/contact</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.8</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/sitemap</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.5</priority>\n';
  sitemap += '  </url>\n';
  
  // Add service pages
  sitemap += '  <!-- Service pages -->\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/services/website-design</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/services/photography</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/services/marketing</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/services/seo</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/services/branding</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/services/software</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/services/real-estate-media</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/services/tech-coaching</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/services/virtual-tours</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/services/led-printing</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  
  // Add location pages
  sitemap += '  <!-- Location pages -->\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/locations/baton-rouge</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.6</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/locations/gonzales</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.6</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/locations/prairieville</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.6</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/locations/ascension-parish</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.6</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/locations/geismar</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.6</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/locations/dutchtown</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.6</priority>\n';
  sitemap += '  </url>\n';
  
  // Add directory pages with more comprehensive entries
  sitemap += '  <!-- Web design directories -->\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/web-design</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/web-design/directories</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/web-design-services</loc>\n';
  sitemap += '    <changefreq>monthly</changefreq>\n';
  sitemap += '    <priority>0.7</priority>\n';
  sitemap += '  </url>\n';
  
  // Add all city-industry pages
  sitemap += '  <!-- City-Industry web design landing pages -->\n';
  for (const url of cityIndustryUrls) {
    sitemap += '  <url>\n';
    sitemap += `    <loc>https://astrem.co${url}</loc>\n`;
    sitemap += '    <changefreq>monthly</changefreq>\n';
    sitemap += '    <priority>0.6</priority>\n';
    sitemap += '  </url>\n';
  }
  
  // Add blog index
  sitemap += '  <!-- Blog index -->\n';
  sitemap += '  <url>\n';
  sitemap += '    <loc>https://astrem.co/blog</loc>\n';
  sitemap += '    <changefreq>weekly</changefreq>\n';
  sitemap += '    <priority>0.8</priority>\n';
  sitemap += '  </url>\n';
  
  // Close the XML document
  sitemap += '</urlset>';
  
  return sitemap;
}

/**
 * Writes the generated main sitemap to the public/sitemap.xml file
 * This function is meant to be called during the build process
 */
export function writeMainSitemap() {
  return generateMainSitemap();
}
